import AccessTime from "@mui/icons-material/AccessTime";
import CheckCircle from "@mui/icons-material/CheckCircle";
import Edit from "@mui/icons-material/Edit";
import FiberManualRecord from "@mui/icons-material/FiberManualRecord";
import MenuBook from "@mui/icons-material/MenuBook";
import Timer from "@mui/icons-material/Timer";
import CloseIcon from "@mui/icons-material/Close";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import Layout from '../../components/layout/Layout';
import { getData, getPresignedUrl, uploadToS3, postData } from '../../services/apiService';
import { Avatar, Box, Button, Card, CardContent, CardHeader, Chip, Grid, Stack, Typography, Modal, IconButton } from "@mui/material";
import React, { useEffect, useState, useCallback } from "react";
import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend, ArcElement, } from 'chart.js';
import { Bar, Doughnut } from 'react-chartjs-2';
import { useDropzone } from 'react-dropzone';
import Progress from '../../hooks/useProgressBar';

// Register ChartJS components
ChartJS.register(
    CategoryScale,
    LinearScale,
    BarElement,
    Title,
    Tooltip,
    Legend,
    ArcElement
);

const StudentProfile = () => {
    const [loading, setLoading] = useState(true);
    const [studentData, setStudentData] = useState([]);
    const [enrolledCourses, setEnrolledCourses] = useState([]);
    const [percentage, setPercentage] = useState([]);
    const [showImageModal, setShowImageModal] = useState(false);
    const [selectedFile, setSelectedFile] = useState(null);
    const [previewUrl, setPreviewUrl] = useState(null);

    useEffect(() => {
        getStudentProfile();
    }, []);

    const getStudentProfile = async () => {
        setLoading(true);
        try {
            const response = await getData('/get-student-Profile');
            if (response) {
                setStudentData(response.data);
                setPercentage(response.percentage);
                setEnrolledCourses(response.enrolledCourses);
            }
            setLoading(false);
        } catch (error) {
            console.error('Failed to fetch data', error);
            setLoading(false);
        }
    }

    // Overview data
    const overviewData = [
        {
            title: "Courses Enrollment",
            value: `${enrolledCourses} course`,
            icon: <MenuBook />,
        },
        {
            title: "Course Completed",
            value: `${percentage}% course`,
            icon: <CheckCircle />,
        },
        {
            title: "In Progress",
            value: `${100 - percentage}% course`,
            icon: <AccessTime />,
        },
        // {
        //     title: "Total Hours",
        //     value: "80 Hr",
        //     icon: <Timer />,
        // },
    ];

    // Progress legend data
    const progressLegend = [
        { label: "Completed", color: "#6a6ac9" },
        { label: "In Progress", color: "#7978e9" },
    ];

    // Monthly course data
    const monthlyData = [
        { month: "Jan", value: 4 },
        { month: "Feb", value: 8 },
        { month: "Mar", value: 6 },
        { month: "Apr", value: 5 },
        { month: "May", value: 14 },
        { month: "Jun", value: 7 },
        { month: "Jul", value: 8 },
        { month: "Aug", value: 12 },
        { month: "Sep", value: 6 },
    ];

    const onDrop = useCallback((acceptedFiles) => {
        const file = acceptedFiles[0];
        if (file) {
            setSelectedFile(file);
            setPreviewUrl(URL.createObjectURL(file));
        }
    }, []);

    const { getRootProps, getInputProps, isDragActive } = useDropzone({
        onDrop,
        accept: {
            'image/*': ['.jpeg', '.jpg', '.png', '.gif']
        },
        maxFiles: 1
    });

    const handleImageUpload = async () => {
        if (!selectedFile) return;

        try {
            // Get presigned URL for S3 upload
            const { uploadUrl, fileKey, publicUrl } = await getPresignedUrl(selectedFile.type);

            // Upload to S3
            await uploadToS3(uploadUrl, selectedFile);

            // Update profile with the new image URL
            const response = await postData('/update-profile-image', {
                profileImage: publicUrl,
                fileKey
            });

            if (response) {
                setStudentData(prev => ({ ...prev, profileImage: publicUrl }));
                setShowImageModal(false);
                setSelectedFile(null);
                setPreviewUrl(null);
            }
            getStudentProfile();
        } catch (error) {
            console.error('Failed to upload image', error);
        }
    };

    return (
        <Layout>
            {loading ? <Progress /> :
                <Box className="p-6 bg-gray-100 min-h-screen">
                    <Box sx={{ width: "100%", p: 3, bgcolor: "#ffffff", borderRadius: "10px", }} >
                        <Typography variant="h5" sx={{ mb: 2 }}>
                            View Profile
                        </Typography>

                        <Grid container spacing={3}>
                            {/* Student Profile Card */}
                            <Grid size={{ xs: 12, md: 4 }}>
                                <Card elevation={1}>
                                    <Box
                                        sx={{
                                            height: 80, background: "linear-gradient(90deg, rgba(10,68,175,0.6) 0%, rgba(181,205,249,1) 100%)",
                                            borderRadius: "10px 10px 0 0", position: "relative",
                                        }} >
                                        {/* <Button variant="contained" size="small" startIcon={<Edit />}
                                            sx={{
                                                position: "absolute", right: 16, top: 9, bgcolor: "white", color: "text.secondary",
                                                "&:hover": { bgcolor: "white", opacity: 0.9 },
                                            }}>
                                            Edit
                                        </Button> */}
                                    </Box>

                                    <Box sx={{ position: "relative", mt: -5, ml: 2 }}>
                                        <Box sx={{ position: 'relative', display: 'inline-block' }}>

                                            <Avatar
                                                src={studentData?.profileImage || ''}
                                                sx={{ width: 80, height: 80, border: "3px solid white" }}
                                            />
                                            <IconButton
                                                sx={{
                                                    position: 'absolute',
                                                    bottom: 0,
                                                    right: 0,
                                                    backgroundColor: 'rgba(0, 0, 0, 0.5)',
                                                    '&:hover': {
                                                        backgroundColor: 'rgba(0, 0, 0, 0.7)',
                                                    },
                                                }}
                                                size="small"
                                            >
                                                <Edit sx={{ color: 'white', fontSize: 16 }} onClick={() => setShowImageModal(true)} />
                                            </IconButton>
                                        </Box>
                                        <Chip size="small" label={studentData?.role_id?.role_name} color="success"
                                            sx={{ position: "absolute", right: 16, bottom: 0 }}
                                        />
                                    </Box>

                                    <CardContent>
                                        <Stack spacing={1} sx={{ mt: 1 }}>
                                            <Typography variant="body2" color="text.secondary">
                                                Name: {studentData.firstName} {studentData.lastName}
                                            </Typography>
                                            <Typography variant="body2" color="text.secondary">
                                                Email: {studentData.email}
                                            </Typography>
                                            <Typography variant="body2" color="text.secondary">
                                                Contact No: {studentData.phone}
                                            </Typography>
                                        </Stack>
                                    </CardContent>
                                </Card>

                                {/* Course Progress Card */}
                                <Card elevation={1} sx={{ mt: 3 }}>
                                    <CardHeader title="Course Progress" />
                                    <CardContent>
                                        <Box
                                            sx={{
                                                display: "flex", justifyContent: "center", alignItems: "center",
                                                position: "relative", mb: 3, height: 200,
                                            }} >
                                            <Doughnut
                                                data={{
                                                    labels: ['Completed', 'In Progress'],
                                                    datasets: [{
                                                        data: [percentage, (100 - percentage)],
                                                        backgroundColor: ['#6a6ac9', '#7978e9'],
                                                        borderWidth: 0,
                                                    }]
                                                }}
                                                options={{
                                                    cutout: '70%', responsive: true, maintainAspectRatio: false,
                                                    plugins: {
                                                        legend: {
                                                            display: false,
                                                        },
                                                    },
                                                }} />
                                            <Box sx={{
                                                position: 'absolute', top: '50%', left: '50%',
                                                transform: 'translate(-50%, -50%)', textAlign: 'center',
                                            }} >
                                                <Typography variant="h5" fontWeight="bold">
                                                    {percentage}%
                                                </Typography>
                                            </Box>
                                        </Box>

                                        <Stack spacing={2} alignItems="center">
                                            {progressLegend.map((item, index) => (
                                                <Stack key={index} direction="row" spacing={1} alignItems="center" >
                                                    <FiberManualRecord sx={{ color: item.color, fontSize: 10 }} />
                                                    <Typography variant="body2" color="text.secondary">
                                                        {item.label}
                                                    </Typography>
                                                </Stack>
                                            ))}
                                        </Stack>
                                    </CardContent>
                                </Card>
                            </Grid>

                            {/* Right Column */}
                            <Grid size={{ xs: 12, md: 8 }}>
                                {/* Overview Card */}
                                <Card elevation={1}>
                                    <CardHeader title="Overview" />
                                    <CardContent>
                                        <Grid container spacing={2}>
                                            {overviewData.map((item, index) => (
                                                <Grid size={{ xs: 12, sm: 6 }} key={index}>
                                                    <Card
                                                        variant="outlined"
                                                        sx={{
                                                            bgcolor: "#eff4fd",
                                                            border: "1.4px solid #dbe7fd",
                                                            borderRadius: "5px",
                                                        }}
                                                    >
                                                        <CardContent>
                                                            <Stack direction="row" spacing={2}>
                                                                <Box sx={{ color: "primary.main" }}>{item.icon}</Box>
                                                                <Box>
                                                                    <Typography variant="subtitle1">
                                                                        {item.title}
                                                                    </Typography>
                                                                    <Typography
                                                                        variant="body2"
                                                                        color="text.secondary"
                                                                        sx={{ mt: 0.5 }}
                                                                    >
                                                                        {item.value}
                                                                    </Typography>
                                                                </Box>
                                                            </Stack>
                                                        </CardContent>
                                                    </Card>
                                                </Grid>
                                            ))}
                                        </Grid>
                                    </CardContent>
                                </Card>

                                {/* My Courses Card */}
                                <Card elevation={1} sx={{ mt: 3 }}>
                                    <CardHeader title="My Courses" />
                                    <CardContent>
                                        <Box sx={{ height: 300, mt: 2 }}>
                                            <Bar
                                                data={{
                                                    labels: monthlyData.map(data => data.month),
                                                    datasets: [
                                                        {
                                                            label: 'Courses',
                                                            data: monthlyData.map(data => data.value),
                                                            backgroundColor: 'rgba(106, 106, 201, 0.6)',
                                                            borderColor: 'rgba(106, 106, 201, 1)',
                                                            borderWidth: 1,
                                                        },
                                                    ],
                                                }}
                                                options={{
                                                    responsive: true,
                                                    maintainAspectRatio: false,
                                                    plugins: {
                                                        legend: {
                                                            display: false,
                                                        },
                                                    },
                                                    scales: {
                                                        y: {
                                                            beginAtZero: true,
                                                            max: 20,
                                                            ticks: {
                                                                stepSize: 2,
                                                            },
                                                        },
                                                    },
                                                }}
                                            />
                                        </Box>
                                    </CardContent>
                                </Card>
                            </Grid>
                        </Grid>
                    </Box>
                </Box>
            }
            {/* Image Upload Modal */}
            <Modal
                open={showImageModal}
                onClose={() => {
                    setShowImageModal(false);
                    setSelectedFile(null);
                    setPreviewUrl(null);
                }}
                aria-labelledby="image-upload-modal"
            >
                <Box sx={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    width: 400,
                    bgcolor: 'background.paper',
                    boxShadow: 24,
                    p: 4,
                    borderRadius: 2,
                }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                        <Typography variant="h6" component="h2">
                            Update Profile Image
                        </Typography>
                        <IconButton
                            onClick={() => {
                                setShowImageModal(false);
                                setSelectedFile(null);
                                setPreviewUrl(null);
                            }}
                            sx={{ position: 'absolute', right: 8, top: 8, color: 'gray' }}
                        >
                            <CloseIcon />
                        </IconButton>
                    </Box>

                    <Box
                        {...getRootProps()}
                        sx={{
                            border: '2px dashed',
                            borderColor: isDragActive ? 'primary.main' : 'grey.300',
                            borderRadius: 1,
                            p: 3,
                            textAlign: 'center',
                            cursor: 'pointer',
                            mb: 2,
                            bgcolor: isDragActive ? 'action.hover' : 'background.paper',
                        }}
                    >
                        <input {...getInputProps()} />
                        <CloudUploadIcon sx={{ fontSize: 40, color: 'primary.main', mb: 1 }} />
                        <Typography>
                            {isDragActive
                                ? "Drop the image here"
                                : "Drag and drop an image here, or click to select"}
                        </Typography>
                    </Box>

                    {previewUrl && (
                        <Box sx={{ mb: 2, textAlign: 'center' }}>
                            <img
                                src={previewUrl}
                                alt="Preview"
                                style={{
                                    maxWidth: '100%',
                                    maxHeight: '200px',
                                    borderRadius: '4px',
                                }}
                            />
                        </Box>
                    )}

                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
                        <Button
                            variant="outlined"
                            onClick={() => {
                                setShowImageModal(false);
                                setSelectedFile(null);
                                setPreviewUrl(null);
                            }}
                        >
                            Cancel
                        </Button>
                        <Button
                            variant="contained"
                            onClick={handleImageUpload}
                            disabled={!selectedFile}
                        >
                            Upload
                        </Button>
                    </Box>
                </Box>
            </Modal>
        </Layout>
    );
};

export default StudentProfile;